import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    InputBase,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    ThemeProvider,
    Tooltip,
} from '@mui/material';
import axios, { AxiosResponse } from 'axios';
import BasicTable, { Column } from '../../components/table';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { useNavigate } from 'react-router-dom';
import { BASEURL } from "../../constants/Common"
import getTheme from '../../theme/theme';
import { Serviceticketfields } from '../../constants/Serviceticketfields';
import FloatingButton from '../../pages/usersupport/helpdesk/FloatingButton';

export interface servicetickets {
    id: number | null;
    ticketno: string;
    assignedto: string;
    createddate: string;
    projectcode: string;
    status: string;
    title: string;
    description: string;
    projectname: string;
    createdby: string;
    priority: string;
    remarks: string;
    attachments: File | null;
    attachments2: File | null;
    attachments3: File | null;
    attachments4: File | null;
    attachments5: File | null;
    attachments6: File | null;
    project_id: string;
    signature: string;
    initials: string;
}

export interface Ticket {
    id: number;
    ticketno: string;
    title: string;
    status: string;
    assignedto: string;
    description: string;
    projectname: string;
    project_id: string;
    createdby: string;
    createddate: string;
    priority: string;
    remarks: string;
    attachments: string | null;
    signature: string;
    initials: string;
}

interface ProjectNames {
    id: number;
    project_name: string;
}

const ServiceticketsPage: React.FC<{ projectNameList: ProjectNames[] }> = ({ projectNameList }) => {
    const [servicetickets, setServicetickets] = useState<servicetickets[]>([]);
    const [filtereddata, setFiltereddata] = useState<servicetickets[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [openPopup, setOpenPopup] = useState<boolean>(false);
    const [newdata, setNewdata] = useState<servicetickets>({
        id: null,
        ticketno: '',
        assignedto: '',
        createddate: '',
        projectcode: '',
        status: '',
        title: '',
        description: '',
        createdby: '',
        priority: '',
        remarks: '',
        projectname: '',
        attachments: null,
        attachments2: null,
        attachments3: null,
        attachments4: null,
        attachments5: null,
        attachments6: null,
        project_id: '',
        signature: '',
        initials: ''
    });

    const [selecteddata, setSelecteddata] = useState<servicetickets | null>(null);
    const [editMode, setEditMode] = useState<boolean>(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const selectedProjectTitle = useSelector((state: RootState) => state.project.selectedProjectTitle);
    const [projectNamesData, setProjectNamesData] = useState<ProjectNames[]>([]);
    const orgid = localStorage.getItem('orgid');
    const userid = localStorage.getItem('userid');
    const subscriptionstatus = localStorage.getItem('subscriptionstatus');
    const userraccess = JSON.parse("" + localStorage.getItem('userrole'));
    const moduleAccess = userraccess?.modulesaccess;
    const filteredRows = filtereddata.filter((data) =>
        selectedProjectTitle ? data.project_id === selectedProjectTitle : true
    );

    useEffect(() => {
        const fetchServiceTickets = async () => {
            try {
                const url = `${BASEURL}/servicetickets/?orgid=${orgid}&userid=${userid}`;
                const response = await axios.get(url, {
                    headers: {
                        Accept: 'application/json',
                    },
                });
                setServicetickets(response.data.servicetickets);
            } catch (error) {
                console.log(error);
            }
        };
        fetchServiceTickets();
        setProjectNamesData(projectNameList)
    }, []);

    useEffect(() => {
        if (selectedProjectTitle == '0') {
            setFiltereddata(servicetickets.filter((data) => (data.project_id !== selectedProjectTitle)));
        } else {
            setFiltereddata(servicetickets.filter((data) => (data.project_id === selectedProjectTitle)));
        }
    }, [servicetickets, searchTerm, selectedProjectTitle]);

    useEffect(() => {
        setFiltereddata(
            servicetickets.filter((data) =>
                data.ticketno.toLowerCase().includes(searchTerm.toLowerCase()) ||
                data.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                data.assignedto.toLowerCase().includes(searchTerm.toLowerCase()) ||
                data.projectname.toLowerCase().includes(searchTerm.toLowerCase()) ||
                data.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
                data.priority.toLowerCase().includes(searchTerm.toLowerCase()) ||
                data.createddate.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [servicetickets, searchTerm]);

    useEffect(() => {
        fetchProjectnamesList();
    }, []);

    const fetchProjectnamesList = async () => {
        setProjectNamesData(projectNameList);
    };

    const columns: Column[] = [
        { id: 'ticketno', label: 'Ticket No', align: 'left' },
        { id: 'title', label: 'Title', align: 'left' },
        { id: 'status', label: 'Status', align: 'left' },
        { id: 'assignedto', label: 'Assigned To', align: 'left' },
        { id: 'createddate', label: 'Created Date', align: 'left' },
        { id: 'priority', label: 'Priority', align: 'left' },
        { id: 'action', label: '', align: 'left' },
    ];

    const handleSearch = () => {
        console.log('Searching...');
    };

    const rows = filtereddata.map((data, index) => ({
        id: index + 1,
        ticketno: data.ticketno,
        title: data.title,
        status: data.status,
        assignedto: data.assignedto,
        createddate: data.createddate,
        priority: data.priority,
        action: (
            <Button variant='contained' onClick={() => handleViewTicket(data)}>
                {editMode ? 'View' : 'View'}
            </Button>
        ),
    }));

    const handleCreateNew = () => {
        navigate('/createnewserviceticket', { state: { projectNameList: projectNameList } });
    };

    const handlePopupClose = () => {
        setOpenPopup(false);
        setEditMode(false);
    };

    const handleViewTicket = (data: servicetickets) => {
        setSelecteddata(data);
        navigate('/ticket-details', { state: { ticketDetails: data, projectNamesData: projectNamesData } });
    };

    const handleFormChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = event.target;
        if (selecteddata) {
            setSelecteddata((prevdata) => ({
                ...prevdata!,
                [name]: value,
            }));
        } else {
            setNewdata((prevdata) => ({
                ...prevdata!,
                [name]: value,
            }));
        }
    };

    const handleBack = () => {
        setEditMode((prevEditMode) => !prevEditMode);
    };

    const handleFormSubmit = async () => {
        try {
            let response: AxiosResponse<servicetickets>;

            if (selecteddata && selecteddata.id !== null) {
                response = await axios.put(
                    BASEURL + `/servicetickets/${selecteddata.id}/`,
                    selecteddata,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );
            } else {
                response = await axios.post(
                    BASEURL + '/servicetickets/create/',
                    newdata,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );
            }

            if (!selecteddata || selecteddata.id === null) {
                setServicetickets((prevtickets) => [...prevtickets, response.data]);
                setOpenPopup(false);
            } else if (editMode) {
                setServicetickets((prevtickets) =>
                    prevtickets.map((ticket) =>
                        ticket.id === response.data.id ? response.data : ticket
                    )
                );
                setOpenPopup(true);
                setSelecteddata(response.data);
                setEditMode(false);
            }

            setNewdata({
                ...newdata,
                id: response.data.id,
            });

        } catch (error) {
            console.error('Error submitting:', error);
        }
    };

    return (
        <ThemeProvider theme={getTheme()}>
            {(subscriptionstatus === 'Active' || subscriptionstatus === 'Expired') ? (
                <Box sx={{ marginTop: '-60px' }}>
                    {subscriptionstatus === 'Expired' && (
                        <Typography variant="body1" >
                            <h3>Please renew your subscription.</h3>
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant='h5' component='div'>
                            Service Tickets
                        </Typography>
                        <Box sx={{ marginLeft: 'auto' }}>
                            <Tooltip
                                title={
                                    moduleAccess === 'Owner' && subscriptionstatus === 'Active'
                                        ? ''
                                        : 'You do not have access to create a new feature'
                                }
                                placement="top"
                                arrow
                            >
                                <span>
                                    <div style={{ opacity: moduleAccess !== 'Owner' || subscriptionstatus !== 'Active' ? 0.5 : 1 }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => {
                                                if (moduleAccess === 'Owner' && subscriptionstatus === 'Active') {
                                                    handleCreateNew();
                                                }
                                            }}
                                            sx={{ mr: 1 }}
                                        >
                                            Create New
                                        </Button>
                                    </div>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>

                    <Box sx={{ marginTop: '10px', display: 'flex', alignItems: 'center' }}>
                        <InputBase
                            placeholder='Search…'
                            inputProps={{ 'aria-label': 'search' }}
                            sx={{
                                marginRight: '10px',
                                border: '1px solid',
                                borderRadius: '4px',
                                paddingLeft: '10px',
                                ml: '5%',
                                height: '45px'
                            }}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Button variant='contained' onClick={handleSearch}>
                            Search
                        </Button>
                    </Box>

                    <Box sx={{ marginTop: '20px' }}>
                        <BasicTable columns={columns} rows={rows} />
                    </Box>

                    <Dialog open={openPopup} onClose={handlePopupClose}>
                        <DialogTitle>
                            {selecteddata ? 'View Service Ticket' : 'Create New Service Ticket'}
                            <IconButton
                                aria-label='close'
                                onClick={handlePopupClose}
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 8,
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent>
                            {Serviceticketfields.map((field) => (
                                <TextField
                                    key={field.name}
                                    fullWidth
                                    margin='normal'
                                    label={field.title}
                                    name={field.name}
                                    type={field.type}
                                    value={
                                        selecteddata
                                            ? selecteddata[field.name as keyof servicetickets]
                                            : newdata[field.name as keyof servicetickets] || ''
                                    }
                                    onChange={(e) => handleFormChange(e)}
                                    disabled={!editMode} // Disable fields when not in edit mode
                                    required={field.isRequired}
                                />
                            ))}
                        </DialogContent>
                        <DialogActions>
                            <Button variant='contained' onClick={editMode ? handleBack : handlePopupClose} color='primary'>
                                Cancel
                            </Button>
                            {!selecteddata && (
                                <Button onClick={handleFormSubmit} color='primary'>
                                    Save
                                </Button>
                            )}
                            {selecteddata && (
                                <>
                                    {!editMode && (
                                        <Button onClick={() => setEditMode(true)} color='primary'>
                                            Edit
                                        </Button>
                                    )}
                                    {editMode && (
                                        <>
                                            <Button onClick={handleBack} color='primary'>
                                                Back
                                            </Button>
                                            <Button onClick={handleFormSubmit} color='primary'>
                                                Submit
                                            </Button>
                                        </>
                                    )}
                                </>
                            )}
                        </DialogActions>
                    </Dialog>
                    <FloatingButton />
                </Box>
            ) : (
                <Typography variant="body1">
                    <h3>Please purchase a subscription plan.</h3>
                </Typography>
            )}
        </ThemeProvider>
    );
};

export default ServiceticketsPage;
